import { StatsCard } from "../components/StatsCard";
import { UserCard } from "../components/UserCard";
import React, { useState, useEffect, Fragment, useCallback } from "react";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Dialog, Transition, } from "@headlessui/react";
import api from "../api/api"; // Assuming your API utility is in this path
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import LoadingLogo from "../components/Loading";
import _ from "lodash";
import Pagination from "../components/Pagination";


const Users = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editData, setEditData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Search, Filter, Pagination
    const [searchTerm, setSearchTerm] = useState("");
    const [filter, setFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [meta, setMeta] = useState({
        total: 0,
        per_page: 10,
        current_page: 1,
        last_page: 1,
        from: 1,
        to: 1,
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            const response = await api.get("/users", {
                params: {
                    type: filter === "all" ? undefined : filter,
                    search: searchTerm || undefined,
                    page: currentPage,
                },
            });
            setUsers(response.data);
            setMeta(response.meta);
        } catch (error) {
            toast.error("Failed to fetch");
        } finally {
            setLoading(false);
        }
    };



    // Debounced fetch function to limit calls while typing
    const debouncedFetchData = useCallback(_.debounce(fetchData, 500), [fetchData]);

    // useEffect that calls the debounced function
    useEffect(() => {
        debouncedFetchData();
        return () => debouncedFetchData.cancel();
    }, [
        filter,
        currentPage,
    ]);




    // Create
    const handleAdd = () => {
        setEditData(null);
        setIsModalOpen(true);
    };

    // Edit
    const handleEdit = (user) => {
        setEditData(user);
        setIsModalOpen(true);
    };

    // Save / Refresh
    const handleSave = () => {
        setIsModalOpen(false);
        fetchData();
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    // Pagination
    const handleNextPage = () => {
        if (meta.current_page < meta.last_page) {
            setCurrentPage(meta.current_page + 1);
        }
    };

    const handlePrevPage = () => {
        if (meta.current_page > 1) {
            setCurrentPage(meta.current_page - 1);
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= meta.last_page; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`px-3 py-1 rounded-lg border border-tblue ${i === meta.current_page ? "bg-tblue text-white" : "bg-white text-gray-700"
                        } hover:bg-tblue hover:text-white mx-1`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this user?")) {
            try {
                await api.delete(`/users/${id}`);
                toast.success("User deleted successfully.");
                fetchData();
            } catch (error) {
                toast.error("Failed to delete user.");
            }
        }
    };

    if (loading) {
        return <LoadingLogo />;
    }

    return (
        <div className="p-4 sm:p-6">
            {/* Top Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4">
                {/* Left Section: Title */}
                <div className="w-full sm:w-auto">
                    <h1 className="text-2xl sm:text-3xl font-bold text-tblue">User Management</h1>
                </div>

                {/* Right Section: Add New User */}
                <div className="w-full sm:w-auto">
                    <button
                        onClick={handleAdd}
                        className="w-full sm:w-auto px-4 py-2 text-white bg-purple-600 rounded-lg text-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors duration-200"
                    >
                        + Add New User
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-12">
                <StatsCard title="Total Users" value={`${meta.total}`} />
                <StatsCard title="Active" value="0" />
                <StatsCard title="Viewed" value="0" />
            </div>

            {/* Search and Filter Section */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6">
                {/* Search Field */}
                <div className="relative flex-grow">
                    <input
                        type="text"
                        placeholder="Search by name, email, phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                setCurrentPage(1);
                                fetchData();
                            }
                        }}
                        className="w-full py-2 pl-4 pr-10 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <svg
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 10-14 0 7 7 0 0014 0z" />
                    </svg>
                </div>

                {/* Filter Dropdown */}
                <select
                    value={filter}
                    onChange={(e) => {
                        setFilter(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="w-full sm:w-auto min-w-[150px] py-2 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                >
                    <option value="all">All Users</option>
                    <option value="user">Clients</option>
                    <option value="walker">Walkers</option>
                    <option value="driver">Drivers</option>
                </select>
            </div>

            {/* User List */}
            <div className="space-y-4">
                {users.map((user) => (
                    <UserCard
                        key={user.id}
                        user={user}
                        handleEdit={() => handleEdit(user)}
                        handleDelete={() => handleDelete(user.id)}
                    />
                ))}
            </div>

            {/* Smart Pagination */}
            <Pagination
                meta={meta}
                onPageChange={(page) => setCurrentPage(page)}
            />

            {/* Modal remains unchanged */}
            {isModalOpen && (
                <CRUModal
                    item={editData}
                    onSave={handleSave}
                    onCancel={handleCancel}
                />
            )}
        </div>
    );
};

export default Users;



// Possible roles we want to assign
const ROLE_PRESETS = [
    { label: "Client", value: ["user"] },
    { label: "Walker", value: ["walker"] },
    { label: "Driver", value: ["driver"] },
    { label: "Dual (Walker & Driver)", value: ["walker", "driver"] },
];


export const CRUModal = ({ item, onSave, onCancel }) => {

    const [selectedPreset, setSelectedPreset] = useState("Only User");

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        dob: "",
        phone: "+971", // Default to Dubai
        roles: ["user"], // default role
    });

    useEffect(() => {
        if (item) {
            const currentRoles = item.roles || ["user"]; // e.g. ["walker", "driver"]
            // Find which preset matches the array exactly (or default to "Only User")
            const matchingPreset = ROLE_PRESETS.find((preset) => {
                // Quick check: same length & same members
                return (
                    preset.value.length === currentRoles.length &&
                    preset.value.every((r) => currentRoles.includes(r))
                );
            }) || ROLE_PRESETS[0];

            setFormData({
                name: item.name || "",
                email: item.email || "",
                dob: item.dob || "",
                phone: item.phone || "+971",
                roles: currentRoles,
                password: "", // blank by default for security
            });
            setSelectedPreset(matchingPreset.label);
        }
    }, [item]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value) => {
        setFormData((prev) => ({ ...prev, phone: value }));
    };

    const handlePresetChange = (e) => {
        const label = e.target.value;
        setSelectedPreset(label);

        // find the corresponding roles array
        const found = ROLE_PRESETS.find((p) => p.label === label);
        if (found) {
            setFormData({ ...formData, roles: found.value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (item) {
                // Update
                await api.post(`/users/${item.id}`, {
                    ...formData,
                    _method: "PUT",
                    // formData.roles => array of roles
                    roles: formData.roles,
                    type: "user", // or omit if you are multi-type
                });
                toast.success("User updated successfully");
            } else {
                // Create
                await api.post(`/users`, {
                    ...formData,
                    roles: formData.roles,
                    type: "user",
                });
                toast.success("User created successfully");
            }
            onSave();
        } catch (error) {
            toast.error("Failed to save user.");
        }
    };

    return (
        <Transition appear show={true} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onCancel}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden 
                                       rounded-2xl bg-white p-6 text-left align-middle
                                       shadow-xl transition-all">
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-medium leading-6 text-gray-900"
                                >
                                    {item ? "Edit User" : "Add New User"}
                                </Dialog.Title>

                                <div className="mt-4">
                                    <form onSubmit={handleSubmit}>
                                        {/* Name */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Name
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="block w-full p-2 border rounded-lg"
                                            />
                                        </div>

                                        {/* Email */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="block w-full p-2 border rounded-lg"
                                            />
                                        </div>

                                        {/* Password (optional if editing) */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Password
                                            </label>
                                            <input
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                required={!item}
                                                className="block w-full p-2 border rounded-lg"
                                                placeholder={
                                                    item ? "Leave blank to keep current password" : ""
                                                }
                                            />
                                        </div>

                                        {/* Phone */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Phone
                                            </label>
                                            <PhoneInput
                                                country={"ae"}
                                                value={formData.phone}
                                                onChange={handlePhoneChange}
                                                inputClass="!w-full p-5 !pl-12 "
                                                searchPlaceholder="Search countries"
                                                enableSearch={true}
                                                containerClass="!w-full !border !rounded-lg flex"
                                                dropdownClass="!bg-white !shadow-lg !rounded-lg !p-2"
                                                autoFormat={true}
                                            />
                                        </div>

                                        {/* DOB */}
                                        {/* <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Date of Birth
                                            </label>
                                            <input
                                                type="date"
                                                name="dob"
                                                value={formData.dob}
                                                onChange={handleChange}
                                                className="block w-full p-2 border rounded-lg"
                                            />
                                        </div> */}

                                        {/* Roles Selection (checkboxes) */}
                                        {/* Role Preset */}
                                        <div className="mb-4">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Role Configuration
                                            </label>
                                            <select
                                                value={selectedPreset}
                                                onChange={handlePresetChange}
                                                className="block w-full p-2 border rounded-lg"
                                            >
                                                {ROLE_PRESETS.map((preset) => (
                                                    <option key={preset.label} value={preset.label}>
                                                        {preset.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Buttons */}
                                        <div className="flex justify-end mt-6">
                                            <button
                                                type="button"
                                                onClick={onCancel}
                                                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg mr-2"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                                            >
                                                {item ? "Update" : "Save"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};




